import { Fragment, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Col, Row } from 'antd';

import { ENV_VARS } from '@/env';
import { routeFileSelector } from '@/utilz';
import type { RootState } from '@/redux/RootReducer';

import VerticalLine from '../../../../components/Bars/VerticalLine';
import FooterContentCard from '../../../common/FooterContentCard';
import { type QuoteFooterStatusKey, quoteStatusIconConfig } from './config';

export interface QuoteTotalItem {
  totalAreaName: QuoteFooterStatusKey;
  count: number;
}

export const QuoteFooter = memo(() => {
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const totals: QuoteTotalItem[] = useSelector((state: RootState) => state.shipment.quoteSearchList.totals);
  const { progressing } = useSelector((state: RootState) => state.shipment.quoteSearchList);

  const filteredTotals = useMemo(() => {
    return totals
      ?.filter(({ totalAreaName }: { totalAreaName: QuoteFooterStatusKey }) => quoteStatusIconConfig[totalAreaName])
      .sort(
        (a, b) =>
          (quoteStatusIconConfig[a.totalAreaName]?.order ?? Number.POSITIVE_INFINITY) -
          (quoteStatusIconConfig[b.totalAreaName]?.order ?? Number.POSITIVE_INFINITY),
      );
  }, [totals]);

  if (progressing) {
    return null;
  }

  return (
    <div>
      <Row
        justify="space-around"
        className={`${
          ENV_VARS.REACT_APP_THEME === 'halliburton' ? 'hb-bg-color' : ''
        } footer-container background-header px-20 py-12`}
        style={{ minHeight: '64px' }}
      >
        {filteredTotals?.map(({ totalAreaName, count }, index) => {
          const config = quoteStatusIconConfig[totalAreaName];
          if (!config) {
            return null;
          }
          const { Icon, translationKey } = config;

          return (
            <Fragment key={totalAreaName}>
              <Col data-testid={`search-quote-footer-${totalAreaName}`}>
                <FooterContentCard
                  count={
                    <NumericFormat
                      thousandsGroupStyle="thousand"
                      displayType="text"
                      value={String(count ?? 0)}
                      thousandSeparator
                    />
                  }
                  label={t(translationKey)}
                  Icon={Icon}
                  color="transparent"
                />
              </Col>
              {index !== filteredTotals.length - 1 ? (
                <Col>
                  <VerticalLine />
                </Col>
              ) : null}
            </Fragment>
          );
        })}
      </Row>
    </div>
  );
});
